import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import apiService from '../../services/api.js';
import './ProjectTable.css';
import StatusPill from './StatusPill.js';
import EditDialog from './EditDialog';
import RuddrProjectDetails from './RuddrProjectDetails';
import ManageRuddrContactsDialog from './ManageRuddrContactsDialog';
// import SendDialog from './SendDialog';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faSort, faSortUp, faSortDown, faAngleLeft, faAngleRight, faWrench } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import SearchIcon from '@mui/icons-material/Search';
import { TextField, CircularProgress } from '@mui/material';
import ColumnOptions from './ColumnOptions';

const deleteIcon = apiService.getBlobAssetUrl('delete_12.svg');
const editIcon = apiService.getBlobAssetUrl('edit_12.svg');
const sendIcon = apiService.getBlobAssetUrl('send_12.svg');
const exportIcon = apiService.getBlobAssetUrl('download.svg');
const addIcon = apiService.getBlobAssetUrl('Add_Icon.svg');
const Iconwrench = apiService.getBlobAssetUrl('icon-wrench.svg');

library.add(faSort, faSortUp, faSortDown, faAngleLeft, faAngleRight, faWrench);

const mapProjectContacts = (contacts, fallbackFullName, fallbackEmail, fallbackContactIds) => {
  if (!contacts) {
    return {
      fullNames: fallbackFullName || '',
      emails: fallbackEmail || '',
      contactIds: fallbackContactIds || ''
    };
  }

  return {
    fullNames: contacts.map(contact => contact.full_name).join(', '),
    emails: contacts.map(contact => contact.email).join(', '),
    contactIds: contacts.map(contact => contact.id).join(',')
  };
};

const matchesEndingInFilter = (rawEndDate, endingInFilter) => {
  if (!endingInFilter) return true;
  if (!rawEndDate || rawEndDate === 'N/A') return false;

  try {
    const endDate = new Date(rawEndDate);
    if (isNaN(endDate.getTime())) return false;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const days = parseInt(endingInFilter, 10);
    const targetDate = new Date(today);
    targetDate.setDate(today.getDate() + days);
    return endDate >= today && endDate <= targetDate;
  } catch (error) {
    console.warn('Error parsing EndDate for filter:', rawEndDate, error);
    return false;
  }
};

const getSortValue = (value, key) => {
  if (key === 'Start Date' || key === 'End Date') {
    return value ? new Date(value) : new Date(0);
  }
  if (key === 'CSAT') {
    return parseFloat(value) || 0;
  }
  return value.toString().toLowerCase();
};

const sortProjects = (projects, sortConfig) => {
  if (!sortConfig.key) return projects;

  return [...projects].sort((a, b) => {
    const aValue = getSortValue(a[sortConfig.key] || '', sortConfig.key);
    const bValue = getSortValue(b[sortConfig.key] || '', sortConfig.key);

    if (aValue instanceof Date && bValue instanceof Date) {
      if (isNaN(aValue.getTime())) return sortConfig.direction === 'asc' ? -1 : 1;
      if (isNaN(bValue.getTime())) return sortConfig.direction === 'asc' ? 1 : -1;
    }

    return sortConfig.direction === 'asc'
      ? aValue < bValue ? -1 : aValue > bValue ? 1 : 0
      : aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
  });
};

const formatDate = (dateString) => {
  try {
    const date = new Date(dateString);
    if (!isNaN(date.getTime())) {
      return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
    }
    return null;
  } catch (error) {
    console.warn('Invalid date format:', dateString, error);
    return null;
  }
};

const updateProjectFields = (project, updatedData, optimisticData) => {
  const updatedFields = updatedData.updated_fields;
  const startDate = updatedFields.project_start_date;
  const endDate = updatedFields.endDate;
  const formattedStartDate = startDate ? formatDate(startDate) : project['Start Date'];
  const formattedEndDate = endDate ? formatDate(endDate) : project['End Date'];

  const { fullNames, emails, contactIds } = mapProjectContacts(
    updatedFields.project_customer_contacts,
    project.FullName,
    project.Email,
    project.CustomerContactIDs
  );

  return {
    ...project,
    'Project Name': optimisticData.projectName || project['Project Name'],
    Status: optimisticData.projectStatus || project.Status,
    Type: optimisticData.projectType || project.Type,
    'Start Date': formattedStartDate,
    'End Date': formattedEndDate,
    Description: optimisticData.projectDescription || project.Description,
    rawEndDate: updatedFields.endDate || project.rawEndDate,
    Practice: project.Practice,
    Client: project.Client,
    FullName: fullNames,
    Email: emails,
    CustomerContactIDs: contactIds,
    'Created By': project['Created By'],
    'Response Received?': project['Response Received?'],
    'Response Date': project['Response Date'],
    CSAT: project.CSAT,
  };
};

const ProjectTable = ({
  projects,
  searchQuery,
  setSearchQuery,
  projectTypeFilter,
  endingInFilter,
  practiceFilter,
  setPracticeFilter,
  distinctPractices,
  rowsPerPage = 10,
  onEditClick,
  onSaveClick,
  onDeleteClick,
  onSendClick,
  setSnackbarMessage,
  setSnackbarOpen,
  surveys,
  tableLoading,
  onAddContacts
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedProject, setSelectedProject] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showRuddrDialog, setShowRuddrDialog] = useState(false);
  const [showManageContactsDialog, setShowManageContactsDialog] = useState(false);
  const [contactsDialogMode, setContactsDialogMode] = useState('add');
  const [selectedRuddrProjectName, setSelectedRuddrProjectName] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [localRowsPerPage, setLocalRowsPerPage] = useState(rowsPerPage);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [showColumnOptions, setShowColumnOptions] = useState(false);

  const moduleAccess = JSON.parse(sessionStorage.getItem('module_access') || '{}');
  const hasWriteAccess = moduleAccess.Projects === 'Read/Write';

  const uniqueStatuses = [...new Set(projects.map((project) => project.Status).filter(Boolean))].sort((a, b) => a.localeCompare(b));
  const uniqueTypes = [...new Set(projects.map((project) => project.Type).filter(Boolean))].sort((a, b) => a.localeCompare(b));

  const [tableColumns, setTableColumns] = useState([
    { key: 'Client', label: 'Client', visible: true },
    { key: 'Project Name', label: 'Project Name', visible: true },
    { key: 'Practice', label: 'Practice Name', visible: true },
    { key: 'Start Date', label: 'Start Date', visible: true },
    { key: 'End Date', label: 'End Date', visible: true },
    { key: 'Status', label: 'Status', visible: true },
    { key: 'Type', label: 'Type', visible: true },
    { key: 'Created By', label: 'Created By', visible: false },
    { key: 'Response Received?', label: 'Response Received', visible: false },
    { key: 'Response Date', label: 'Response Date', visible: false },
    { key: 'CSAT', label: 'CSAT', visible: true },
    { key: 'Actions', label: 'Actions', visible: hasWriteAccess }
  ]);

  useEffect(() => {
    console.log('Projects data:', projects);
    if (tableColumns.length === 0) {
      setTableColumns([
        { key: 'Client', label: 'Client', visible: true },
        { key: 'Project Name', label: 'Project Name', visible: true },
        { key: 'Practice', label: 'Practice Name', visible: true },
        { key: 'Start Date', label: 'Start Date', visible: true },
        { key: 'End Date', label: 'End Date', visible: true },
        { key: 'Status', label: 'Status', visible: true },
        { key: 'Type', label: 'Type', visible: true },
        { key: 'Created By', label: 'Created By', visible: false },
        { key: 'Response Received?', label: 'Response Received', visible: false },
        { key: 'Response Date', label: 'Response Date', visible: false },
        { key: 'CSAT', label: 'CSAT', visible: true },
        { key: 'Actions', label: 'Actions', visible: hasWriteAccess }
      ]);
    }
  }, [setSnackbarMessage, setSnackbarOpen]);

  const filteredProjects = useMemo(() => {
    if (!projects) return [];

    const matchesSearch = (row) => Object.values(row).some((value) =>
      value && value.toString().toLowerCase().includes(searchQuery.toLowerCase())
    );

    let filtered = projects.filter((row) =>
      matchesSearch(row)
    );

    filtered = sortProjects(filtered, sortConfig);

    return filtered;
  }, [projects, searchQuery, sortConfig]);

  // Reset to page 1 when projects array changes (filters applied in parent)
  useEffect(() => {
    setCurrentPage(1);
  }, [projects]);

  const totalPages = Math.ceil(filteredProjects.length / localRowsPerPage);

  // Validate current page against total pages
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  }, [totalPages, currentPage]);

  const indexOfLastRow = currentPage * localRowsPerPage;
  const indexOfFirstRow = indexOfLastRow - localRowsPerPage;
  const currentRows = filteredProjects.slice(indexOfFirstRow, indexOfLastRow);


  const maxVisiblePages = 3;

  const getPageRange = () => {
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    return { startPage, endPage };
  };

  const { startPage, endPage } = getPageRange();

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handlePrevious = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handleRowsPerPageChange = (e) => {
    setLocalRowsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const handleDelete = (project) => {
    onDeleteClick(project);
  };

  // Send survey functionality temporarily disabled
  // const [showSendDialog, setShowSendDialog] = useState(false);

  const handleManageContacts = (project) => {
    setSelectedProject(project);
    setContactsDialogMode('edit');
    setShowManageContactsDialog(true);
    if (onAddContacts) {
      onAddContacts(project);
    }
  };

  const handlePracticeChange = (e) => {
    setPracticeFilter(e.target.value);
  };

  const handleSaveEdit = async (updatedData, optimisticData) => {
    try {
      if (onSaveClick) {
        await onSaveClick(updatedData, optimisticData);
      }

      setSnackbarMessage('Project updated successfully.');
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Error updating project:', error);
      setSnackbarMessage(`Failed to update project: ${error.message}`);
      setSnackbarOpen(true);
    } finally {
      setShowEditDialog(false);
      setSelectedProject(null);
    }
  };

  const handleSaveContacts = async (projectData) => {
    try {
      const response = await apiService.fetchData(`projects/customer-contacts/${projectData.project_id}`);
      console.log('API response for contacts:', response);

      const updatedContacts = response?.contacts || response?.project_customer_contacts || [];
      console.log('Extracted contacts:', updatedContacts);

      const fullNames = updatedContacts
        .map((contact) => contact.FirstName || contact.full_name || '')
        .filter((name) => name)
        .join(', ');
      const emails = updatedContacts
        .map((contact) => contact.Email || contact.email || '')
        .filter((email) => email)
        .join(', ');
      const contactIds = updatedContacts
        .map((contact) => contact.ID || contact.id || '')
        .filter((id) => id)
        .join(',');

      console.log('Mapped data:', { fullNames, emails, contactIds });

      if (selectedProject && selectedProject.id === projectData.project_id) {
        setSelectedProject((prev) => ({
          ...prev,
          FullName: fullNames || prev.FullName || '',
          Email: emails || prev.Email || '',
          CustomerContactIDs: contactIds || prev.CustomerContactIDs || '',
        }));
      }

      if (onSaveClick) {
        await onSaveClick(projectData, {
          FullName: fullNames,
          Email: emails,
          CustomerContactIDs: contactIds,
        });
      }

      setSnackbarMessage('Contacts updated successfully.');
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Error updating contacts:', error);
      setSnackbarMessage(`Failed to update contacts: ${error.message}`);
      setSnackbarOpen(true);
    } finally {
      setShowManageContactsDialog(false);
    }
  };

  // const handleSend = (project) => {
  //   setSelectedProject(project);
  //   setShowSendDialog(true);
  // };

  // const handleSaveSend = () => {
  //   try {
  //     setShowSendDialog(false);
  //     setSelectedProject(null);

  //     setSnackbarMessage('Email sent successfully.');
  //     setSnackbarOpen(true);
  //   } catch (error) {
  //     console.error('Error in handleSaveSend:', error);
  //     setSnackbarMessage('Failed to send email.');
  //     setSnackbarOpen(true);
  //   }
  // };

  const exportToCSV = () => {
    try {
      const headers = [
        'Project Name',
        'Client',
        'Practice Name',
        'Start Date',
        'End Date',
        'Status',
        'Type',
        'Created By',
        'Response Received?',
        'Response Date',
        'CSAT'
      ].join(',');

      const rows = filteredProjects.map((row) => {
        const values = [
          row['Project Name'] || '',
          row.Client || '',
          row.Practice || '',
          row['Start Date'] || '',
          row['End Date'] || '',
          row.Status || '',
          row.Type || '',
          row['Created By'] || '',
          row['Response Received?'] || '',
          row['Response Date'] || '',
          row.CSAT || ''
        ];
        return values.map((field) => `"${String(field).replace(/"/g, '""')}"`).join(',');
      });

      const csvContent = [headers, ...rows].join('\n');
      const encodedUri = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csvContent);

      const link = document.createElement('a');
      link.setAttribute('href', encodedUri);
      link.setAttribute('download', 'projects_data.csv');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setSnackbarMessage('Exported to CSV successfully.');
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Error exporting to CSV:', error);
      setSnackbarMessage('Failed to export to CSV.');
      setSnackbarOpen(true);
    }
    setShowExportDropdown(false);
  };

  const exportToPDF = () => {
    try {
      const doc = new jsPDF();
      const tableColumn = [
        'Project Name',
        'Client',
        'Practice Name',
        'Start Date',
        'End Date',
        'Status',
        'Type',
        'Created By',
        'Response Received?',
        'Response Date',
        'CSAT'
      ];
      const tableRows = filteredProjects.map((row) => [
        row['Project Name'] || '',
        row.Client || '',
        row.Practice || '',
        row['Start Date'] || '',
        projectTypeFilter === 'Ruddr Projects' ? '' : row['End Date'] || '',
        row.Status || '',
        row.Type || '',
        row['Created By'] || '',
        row['Response Received?'] || '',
        row['Response Date'] || '',
        row.CSAT || ''
      ]);

      autoTable(doc, {
        head: [tableColumn],
        body: tableRows,
        startY: 20,
        theme: 'striped',
        styles: { fontSize: 10 },
        headStyles: { fillColor: [63, 81, 181] },
      });
      doc.save('projects_data.pdf');
      setSnackbarMessage('Exported to PDF successfully.');
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Error exporting to PDF:', error);
      setSnackbarMessage('Failed to export to PDF.');
      setSnackbarOpen(true);
    }
    setShowExportDropdown(false);
  };

  const handleExportClick = () => {
    setShowExportDropdown((prev) => !prev);
  };

  const handleExportKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      setShowExportDropdown((prev) => !prev);
    }
  };

  const handleExportOptionKeyDown = (event, exportFunction) => {
    if (event.key === 'Enter' || event.key === ' ') {
      exportFunction();
    }
  };

  const handleRuddrProjectClick = (projectName) => {
    setSelectedRuddrProjectName(projectName);
    setShowRuddrDialog(true);
  };

  const getProjectContacts = () => {
    if (!selectedProject) return [];
    return selectedProject.FullName ? selectedProject.FullName.split(', ').filter(name => name.trim()) : [];
  };

  const getProjectEmails = () => {
    if (!selectedProject) return [];
    return selectedProject.Email ? selectedProject.Email.split(', ').filter(email => email.trim()) : [];
  };

  const getProjectContactIds = () => {
    if (!selectedProject) return [];
    return selectedProject.CustomerContactIDs
      ? selectedProject.CustomerContactIDs.split(',').map(id => id.trim()).filter(id => id)
      : [];
  };

  const projectContacts = getProjectContacts();
  const projectEmails = getProjectEmails();
  const projectContactIds = getProjectContactIds();

  return (
    <div className="Project-table-container">
      {tableLoading ? (
        <div className="Project-loading-message">
          <CircularProgress />
        </div>
      ) : (
        <>
          <div className="Project-filters-container">
            <div className="Project-filter-search">
              <input
                className="Project-search-input"
                type="text"
                placeholder="Search Projects..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <SearchIcon className="Project-search-icon" />
            </div>
            <div className="Project-filter-practices">
              <select
                className="Project-filter-select"
                value={practiceFilter}
                onChange={handlePracticeChange}
              >
                <option value="">All Practices</option>
                {distinctPractices.map((practice) => (
                  <option key={practice} value={practice}>
                    {practice}
                  </option>
                ))}
              </select>
            </div>
            <div className="Project-filter-column-options">
              <button
                className="Project-column-options-button"
                onClick={() => setShowColumnOptions(true)}
              >
                Column Options
                <img src={Iconwrench} alt="Wrench" className="Project-column-options-icon" />
              </button>
              {showColumnOptions && (
                <ColumnOptions
                  columns={tableColumns}
                  onSave={(updatedColumns) => {
                    if (updatedColumns) {
                      setTableColumns(updatedColumns);
                    }
                    setShowColumnOptions(false);
                  }}
                />
              )}
            </div>
            <div className="Project-filter-export-results">
              <div className="Project-export-results-inner">
                <div className="Project-export-container">
                  <img
                    src={exportIcon}
                    alt="Export"
                    className="Project-export-icon"
                    role="button"
                    tabIndex={0}
                    onClick={handleExportClick}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleExportClick(); }}
                  />
                  {showExportDropdown && (
                    <div className="Project-export-dropdown">
                      <div className="Project-export-option" onClick={exportToCSV}>
                        Download CSV
                      </div>
                      <div className="Project-export-option" onClick={exportToPDF}>
                        Download PDF
                      </div>
                    </div>
                  )}
                </div>
                <span className="Project-pagination-info">
                  Results: {indexOfFirstRow + 1} - {Math.min(indexOfLastRow, filteredProjects.length)} of {filteredProjects.length}
                </span>
                <div className="Project-entries-box">
                  <select
                    className="Project-entries-select"
                    value={localRowsPerPage}
                    onChange={handleRowsPerPageChange}
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
          <div className="Project-table-wrapper">
            <table className="Project-table">
              <thead>
                <tr>
                  {tableColumns.filter(col => col.visible && col.key !== 'Actions').map(col => (
                    <th key={col.key} className={col.key === 'Practice' ? 'practice-column' : ''}>
                      {col.label}
                      {col.key !== 'Status' && col.key !== 'Practice' && col.key !== 'Response Received?' && col.key !== 'Created By' && (
                        <button className="Project-sort-button" onClick={() => requestSort(col.key)}>
                          <FontAwesomeIcon icon={sortConfig.key === col.key ? (sortConfig.direction === 'asc' ? faSortUp : faSortDown) : faSort} />
                        </button>
                      )}
                    </th>
                  ))}
                  {hasWriteAccess && tableColumns.find(col => col.key === 'Actions')?.visible && (
                    <th key="Actions" className="Project-actions-header">Actions</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {currentRows.map((row) => (
                  <tr key={row.id} className={row.id % 2 === 0 ? 'Project-even-row' : 'Project-odd-row'}>
                    {tableColumns.filter(col => col.visible && col.key !== 'Actions').map(col => (
                      <td key={`${row.id}-${col.key}`} title={row[col.key] || ''}>
                        {col.key === 'Status' ? (
                          <StatusPill status={row[col.key] || 'N/A'} />)
                          // ) : col.key === 'Project Name' && projectTypeFilter === 'Ruddr Projects' ? (
                          //   <button
                          //     onClick={() => handleRuddrProjectClick(row['Project Name'])}
                          //     style={{
                          //       color: '#03519f',
                          //       textDecoration: 'underline',
                          //       background: 'none',
                          //       border: 'none',
                          //       cursor: 'pointer',
                          //       textAlign: 'left',
                          //       fontSize: 'inherit',
                          //       fontFamily: 'inherit',
                          //       padding: 0,
                          //     }}
                          //   >
                          //     {row[col.key]}
                          //   </button>
                          // ) 
                          : (
                            row[col.key] || 'N/A'
                          )}
                      </td>
                    ))}
                    {hasWriteAccess && tableColumns.find(col => col.key === 'Actions')?.visible && (
                      <td className="Project-action-cell">
                        <div className="Project-action-icons">
                          {row.ProjectType === 'ruddr' ? (
                            <button
                              className="Project-action-button Project-edit-contacts-button"
                              onClick={() => handleManageContacts(row)}
                              title="Manage Contacts"
                            >
                              <img src={editIcon} alt="Manage Contacts" className="Project-action-icon" />
                            </button>
                          ) : (
                            <>
                              <button
                                className="Project-action-button Project-edit-button"
                                onClick={() => onEditClick(row)}
                                title="Edit"
                              >
                                <img src={editIcon} alt="Edit" className="Project-action-icon" />
                              </button>
                              {row['Response Received?'] !== 'Yes' && (
                                <button
                                  className="Project-action-button Project-delete-button"
                                  onClick={() => handleDelete(row)}
                                  title="Delete"
                                >
                                  <img src={deleteIcon} alt="Delete" className="Project-action-icon" />
                                </button>
                              )}
                            </>
                          )}
                          {/* Send survey button temporarily disabled
                      {row['Response Received?'] === 'No' && (
                        <button
                          className="Project-action-button Project-send-button"
                          onClick={() => handleSend(row)}
                          title="Send"
                        >
                          <img src={sendIcon} alt="Send" className="Project-action-icon" />
                        </button>
                      )}
                      */}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="Project-table-footer">
            <div className="Project-pagination">
              <button
                onClick={handlePrevious}
                disabled={currentPage <= 1 || totalPages === 0}
                className="Project-pagination-button"
              >
                <FontAwesomeIcon icon={faAngleLeft} />
              </button>
              {Array.from({ length: endPage - startPage + 1 }, (_, i) => {
                const pageNumber = startPage + i;
                const isSelected = currentPage === pageNumber;
                const buttonClass = `Project-pagination-button ${isSelected ? 'Project-selected' : ''}`;
                return (
                  <button key={pageNumber} onClick={() => paginate(pageNumber)} className={buttonClass}>
                    {pageNumber}
                  </button>
                );
              })}
              <button
                onClick={handleNext}
                disabled={currentPage >= totalPages || totalPages === 0}
                className="Project-pagination-button"
              >
                <FontAwesomeIcon icon={faAngleRight} />
              </button>
            </div>
          </div>
        </>
      )}
      {showEditDialog && selectedProject && (
        <EditDialog
          project={selectedProject}
          statuses={uniqueStatuses}
          types={uniqueTypes}
          onClose={() => {
            setShowEditDialog(false);
            setSelectedProject(null);
          }}
          onSave={handleSaveEdit}
        />
      )}
      {/* Send survey dialog temporarily disabled
      {showSendDialog && selectedProject && (
        <SendDialog
          project={selectedProject}
          contacts={projectContacts}
          emails={projectEmails}
          customerContactIds={projectContactIds}
          surveys={surveys || []}
          onClose={() => {
            setShowSendDialog(false);
            setSelectedProject(null);
          }}
          onSave={handleSaveSend}
        />
      )}
      */}
      {showRuddrDialog && (
        <RuddrProjectDetails
          open={showRuddrDialog}
          onClose={() => {
            setShowRuddrDialog(false);
            setSelectedRuddrProjectName('');
          }}
          projectName={selectedRuddrProjectName}
        />
      )}
      {showManageContactsDialog && selectedProject && (
        <ManageRuddrContactsDialog
          open={showManageContactsDialog}
          onClose={() => {
            setShowManageContactsDialog(false);
            setSelectedProject(null);
          }}
          onSave={handleSaveContacts}
          project={selectedProject}
          mode={contactsDialogMode}
        />
      )}
    </div>
  );
};

ProjectTable.propTypes = {
  projects: PropTypes.array.isRequired,
  searchQuery: PropTypes.string.isRequired,
  setSearchQuery: PropTypes.func.isRequired,
  projectTypeFilter: PropTypes.oneOf(['Survey Projects', 'Ruddr Projects']).isRequired,
  endingInFilter: PropTypes.string.isRequired,
  practiceFilter: PropTypes.string,
  setPracticeFilter: PropTypes.func.isRequired,
  distinctPractices: PropTypes.array.isRequired,
  rowsPerPage: PropTypes.number,
  onEditClick: PropTypes.func.isRequired,
  onSaveClick: PropTypes.func.isRequired,
  onDeleteClick: PropTypes.func.isRequired,
  onSendClick: PropTypes.func.isRequired,
  onAddContacts: PropTypes.func.isRequired,
  setSnackbarMessage: PropTypes.func.isRequired,
  setSnackbarOpen: PropTypes.func.isRequired,
  surveys: PropTypes.arrayOf(PropTypes.object).isRequired,
  tableLoading: PropTypes.bool.isRequired,
};

ProjectTable.defaultProps = {
  rowsPerPage: 10,
};

export default ProjectTable;